import { useRef, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import { OneAlert, RichTextEditorHandle } from '@factorialco/f0-react/dist/experimental'
import {
  AtsJobApplicationFilesOfferLetterDocument,
  AtsOfferLetterFromTemplateCreateDocument,
  AtsSendOfferLetterFromTemplateGetFileDocument,
  AtsSenfOfferLetterFromTemplateModalFragment,
} from 'generated/resources/graphql'

import DocumentType from 'modules/core/components/DocumentType'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { TextEditorWithMessageTemplates } from 'modules/ats/components/TextEditorWithMessageTemplates'
import { Statuses } from 'modules/ats/lib/enums'

interface Props {
  applicationData: AtsSenfOfferLetterFromTemplateModalFragment
  onClose: () => void
}

export const SenfOfferLetterFromTemplateModal = ({ applicationData, onClose }: Props) => {
  const richTextEditorRef = useRef<RichTextEditorHandle>(null)
  const { access } = useCurrentSession()
  const [customMessage, setCustomMessage] = useState<string>('')

  const { data } = useQuery(AtsSendOfferLetterFromTemplateGetFileDocument, {
    variables: { applicationId: applicationData.id },
  })

  const [createOfferLetter, { loading: sendingOfferLetter }] = useMutation(
    AtsOfferLetterFromTemplateCreateDocument,
    {
      extractorFn: (response) => response.atsMutations.createAtsOfferLetter,
      refetchQueries: [AtsJobApplicationFilesOfferLetterDocument],
    }
  )
  const file = data?.ats?.filesConnection?.edges?.[0]?.node?.file
  const atsFileId = data?.ats?.filesConnection?.edges?.[0]?.node?.id

  const onSendOfferLetter = async () => {
    const result = await createOfferLetter({
      variables: {
        accessId: access.id,
        atsApplicationId: applicationData.id,
        customMessage: customMessage,
        atsFileId: atsFileId ?? 0,
        status: Statuses.Pending,
      },
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('ats.offer_letter.modal.success_message'),
      })

      onClose()
    }

    return handled
  }

  const requiredData = !!customMessage && !!atsFileId && !!applicationData.candidate.email

  return (
    <F0Dialog
      onClose={onClose}
      isOpen
      title={i18n.t('ats.send_offer_letter_modal.from_template.send_modal.title')}
      description={i18n.t('ats.send_offer_letter_modal.from_template.send_modal.description')}
      onSubmit={onSendOfferLetter}
      primaryAction={{
        label: i18n.t('common.send'),
        onClick: () => onSendOfferLetter(),
        loading: sendingOfferLetter,
        disabled: !requiredData,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: () => onClose(),
      }}
      width="sm"
    >
      {!atsFileId || !applicationData.candidate.email ? (
        <OneAlert
          variant="critical"
          title={i18n.t('ats.send_offer_letter_modal.from_template.send_modal.error_message_title')}
          description={i18n.t(
            'ats.send_offer_letter_modal.from_template.send_modal.error_message_description'
          )}
        />
      ) : (
        <Box gap="s16">
          <OneAlert
            variant="warning"
            title={i18n.t('ats.offer_letter.modal.warning_message_existing_offer_title')}
            description={
              (data?.ats?.offerLettersConnection?.nodes?.length ?? 0) > 0
                ? i18n.t('ats.offer_letter.modal.warning_message_existing_offer')
                : i18n.t('ats.offer_letter.modal.warning_message')
            }
          />
          <Box
            flexDirection="row"
            gap="s2"
            paddingX="s16"
            paddingY="s12"
            justifyContent="spaceBetween"
            width="full"
            alignItems="center"
            borderRadius={{ all: 'abs016' }}
            border={{
              all: {
                width: 's1',
                style: 'solid',
                color: 'grey400',
              },
            }}
          >
            <Box alignItems="center" flexGrow flexDirection="row" gap="s16" maxHeight="s32">
              <DocumentType mimetype={file?.contentType ?? ''} />
              <Box overflowX="hidden" maxWidth="s300">
                <Text.Normal size="200" weight="medium" color="textPrimary">
                  {file?.filename ?? ''}
                </Text.Normal>
              </Box>
            </Box>
          </Box>
          <TextEditorWithMessageTemplates
            richTextEditorRef={richTextEditorRef}
            onChange={(value) => setCustomMessage(value || '')}
            showLabel
            placeholder={i18n.t('ats.offer_letter.modal.custom_message_placeholder', {
              candidate_name: applicationData.candidate.fullName,
            })}
            value={customMessage}
          />
        </Box>
      )}
    </F0Dialog>
  )
}
